import { isEqual } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';
import { IRecordModel } from '../../../../models';
import { useSelector } from '../../../AppStore';

export const MAX_SELECTION_SIZE = parseInt(process.env.REACT_APP_MAX_EXPORT_EVENT_AND_SERVICE ?? '50', 10);

const useSelectAllCheckBox = (
  selectedList: IRecordModel[],
  setIsCheckAllList: (selected: boolean) => void,
  failure: (title: string, message: string) => void,
  setSelectedList: (records: IRecordModel[]) => void,
  item = 10
) => {
  const { t: translate } = useTranslation();
  const { events, services } = useSelector((state) => ({
    events: state.fetchAllEventReducer.events.events,
    services: state.fetchAllServiceReducer.serviceList.services,
  }), shallowEqual);

  const showMaxLimSelectionToast = () => {
    failure(
      translate('toast_component.lbl_error'),
      translate('service_list_wrapper_component.msg_max_export_limit_reached_header', {
        replace: { maxLimit: MAX_SELECTION_SIZE }
      })
    );
  };

  const isSelectedRecordsEqual = () => {
    const allRecords = sortList(recordToCheck);
    const sortedList = sortList(selectedList);
    return isEqual(allRecords, sortedList);
  };

  /**
   * Gets all events records
   * @returns Event records
   */
  const getAllEventsRecord = () => events.map((item) => ({
    recordName: item.eventName,
    recordVersion: parseInt(item.version, 10)
  } as IRecordModel));

  /**
 * Gets all service records
 * @returns Service records
 */
  const getAllServiceRecords = () => services.map((item) => ({
    recordName: item.serviceName,
    recordVersion: parseInt(`${item.version}`, 10)
  } as IRecordModel));

  const recordToCheck = services?.length ? getAllServiceRecords() : getAllEventsRecord();

  /**
   * Sort event/service list based on version and name
   * @param records records
   * @returns sorted records
   */
  const sortList = useMemo(() => (records: IRecordModel[]) => [...records].sort((a, b) => {
    if (a.recordVersion !== b.recordVersion) {
      return a.recordVersion - b.recordVersion;
    }
    return a.recordName.localeCompare(b.recordName);
  }), []);

  const isMaxSelectionSizeReached = () => events.length > MAX_SELECTION_SIZE || services.length > MAX_SELECTION_SIZE || selectedList.length >= MAX_SELECTION_SIZE;

  const handleCheckboxChange = (checked: boolean) => {
    if (!checked) {
      setIsCheckAllList(false);
      setSelectedList([]);
    } else {
      setIsCheckAllList(true);
      setSelectedList(recordToCheck);
    }
  };
  /**
   * Handles all select all checkbox
   * @param checkEvent checkbox select change event
   */
  const handleSelectAllCheckbox = (checkEvent: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = checkEvent.target;
    if (checked && isMaxSelectionSizeReached()) {
      showMaxLimSelectionToast();
      return;
    }
    handleCheckboxChange(checked);
  };

  useEffect(() => {
    // Monitor select all check box selection, if all selected items then check select all checkbox
    const isPageSize = item === selectedList?.length && selectedList?.length <= MAX_SELECTION_SIZE;
    const isMaxSelectionReached = (selectedList?.length === MAX_SELECTION_SIZE && isSelectedRecordsEqual()) || isPageSize;
    setIsCheckAllList(isMaxSelectionReached);
  }, [selectedList, events[0]?.eventName, services[0]?.serviceName]);

  return { handleSelectAllCheckbox, showMaxLimSelectionToast, isSelectedRecordsEqual };
};

export default useSelectAllCheckBox;
